/**
 * Typed wrappers for the platform-side API (dashboard SPA).
 *
 *   GET /api/platform/me   → current user, active client, accessible clients
 *
 * Distinct from /api/auth/* (login flow) and /api/admin/v1/* (admin
 * SPA). Each surface has its own API namespace so blast-radius is
 * contained when one changes.
 */
import { api } from './client';

export interface PlatformUser {
  email: string;
  display_name: string;
  first_name: string;
  last_name: string;
  /** Two-letter initials derived server-side from first/last (or
   *  email fallback). UI never needs to compute this. */
  initials: string;
  /** Drives the dashboard rail's "Go to admin portal" link
   *  visibility: a super admin who hasn't picked a current
   *  client still sees the link. Per-client admin status
   *  comes through `is_admin` at the response root. */
  is_super_admin: boolean;
}

export interface PlatformClient {
  client_id: string;
  /** display_name with name fallback. */
  name: string;
  branding: PlatformBranding;
}

/** Tenant branding the dashboard cares about - applied at the
 *  document root by BrandingApplier on mount, so changes from
 *  /admin/branding take effect after a single page reload.
 *
 *  Every field is intentionally a string (or 0) - empty means "use
 *  the product default for that token". The applier knows to skip
 *  blank values rather than wiping the global var. */
export interface PlatformBranding {
  product_name: string;
  strapline: string;
  logo_url: string;
  favicon_url: string;

  /** Footer "Powered by" line. Empty label → hide attribution. */
  attribution_label: string;
  attribution_name: string;

  /** Per-language overrides of the four text fields above
   *  (product_name, strapline, attribution_label, attribution_name).
   *  Sparse; missing langs / fields fall back to the English value
   *  on this branding object. Empty {} when the tenant hasn't
   *  authored any translations. */
  text_overrides: Record<string, Partial<Record<
    'product_name' | 'strapline' | 'attribution_label' | 'attribution_name',
    string
  >>>;

  primary_color: string;
  secondary_color: string;

  // Extended palette - admins set these via /admin/branding's
  // Advanced section.
  text_color: string;
  text_dim_color: string;
  surface_color: string;
  surface_alt_color: string;
  border_color: string;

  // Catalogue id ('quicksand', 'system-sans', 'google:inter', …).
  // Empty → product default.
  font_display: string;
  font_body: string;
  // Pre-resolved Google Fonts CSS URL. Empty for platform fonts.
  font_display_url: string;
  font_body_url: string;

  radius_sm: number;
  radius_md: number;
  radius_lg: number;

  custom_css: string;
}

export interface AvailableClient {
  client_id: string;
  name: string;
  role: 'admin' | 'user' | string;
}

export interface MeResponse {
  user: PlatformUser;
  current_client: PlatformClient | null;
  available_clients: AvailableClient[];
  /** Whether the active client has the "show source documents in
   *  the chat" feature enabled. Drives the SourcePanel - when
   *  false, source buttons render as plain text rather than
   *  clickable triggers. Mirrors clients/<id>.enable_document_citation_view. */
  citation_view_enabled: boolean;
  /** "Does this user have admin access from where they are right
   *  now?". Super admin → always true. Otherwise true iff they're
   *  an admin for the active client. Drives the rail's "Go to
   *  admin portal" cross-link. Standard users (the most common
   *  case) get false. */
  is_admin: boolean;
  /** Tenant default for how video citations render in chat:
   *    - false (default): collapsed pill button in the source row,
   *      click to expand inline above the chat text.
   *    - true: auto-expanded VideoCitationTile on every chat
   *      response (legacy look).
   *  Toggled from AI Settings → Behaviour. */
  display_video_inline: boolean;
  /** Effective STT provider for the active client (client override >
   *  global default > 'whisper' fallback). MicButton uses this to
   *  pick its transport:
   *    - 'whisper' / 'google'   : record full clip then POST.
   *    - 'google_streaming'     : open a WebSocket; partial transcripts
   *                               appear in the composer as the user
   *                               speaks. Falls back to POST on any
   *                               WebSocket / stream failure.
   *  Optional so older /me responses still parse. */
  stt_provider?: 'whisper' | 'google' | 'google_streaming';
  /** How long of sustained quiet (ms) after speech before the mic
   *  auto-stops. Server-resolved (client > global > 1200ms floor);
   *  the dashboard MicButton reads this and clamps defensively. */
  voice_vad_silence_ms?: number;
  /** Site Vocabulary Phase 4a, fully resolved platform-ui chrome
   *  catalogue, keyed by language code. Server resolves the chain
   *  (built-in defaults → super-admin global overrides → tenant
   *  overrides) so the SPA just looks up `[lang][key]`.
   *
   *  Optional + back-compat: older /me payloads omit this field;
   *  the dashboard's I18nProvider falls back to BUILTIN_EN so
   *  nothing renders 'undefined'. */
  platform_ui_i18n?: Record<string, Record<string, string>>;
}

export function getMe(): Promise<MeResponse> {
  return api<MeResponse>('/api/platform/me');
}
